// ── Per-platform portfolio statistics ─────────────────────────────
//
// Underdog and DraftKings are two different markets with two different draft
// sizes (UD 18 rounds / 216 picks, DK 20 rounds / 240 picks) and two different
// ADP boards. Every number that is a RATE or a COMPARISON must therefore be
// computed inside one platform and only then displayed side by side.
//
// The bug this module exists to kill: exposure was a single blended fraction —
// `player's drafts / ALL drafts`. With 100 Underdog drafts and 10 DraftKings
// drafts, a player rostered in every DK draft read as 9% exposure when the
// truthful figure is 100% of DK. Denominators are per-platform here.

import type { CsvRow, Player, TeamDraft } from "../types/draft";
import { canonicalName } from "./playerIdentity";
import { resolveMarketAdp } from "./playerHelpers";

/** Display order for platform columns, chips and legends. */
export const PLATFORM_ORDER = ["Underdog", "DraftKings", "Unknown"];

/** Sort platform labels into the canonical display order. */
export function sortPlatforms(providers: string[]): string[] {
  return [...providers].sort(
    (a, b) => (PLATFORM_ORDER.indexOf(a) + 1 || 99) - (PLATFORM_ORDER.indexOf(b) + 1 || 99)
  );
}

/** Provider label for a row, matching DraftDataContext's `rowProvider`. */
function providerOf(row: CsvRow): string {
  return (row["Provider"] || "").trim() || "Unknown";
}

/**
 * Distinct drafts per platform — the denominators for every exposure figure.
 * Computed over the SAME filtered row set the table is showing, so exposure
 * always reads against the drafts actually in view.
 */
export function draftTotalsByProvider(rows: CsvRow[]): Record<string, number> {
  const sets: Record<string, Set<string>> = {};
  for (const row of rows) {
    const p = providerOf(row);
    (sets[p] ??= new Set()).add(row["Draft"]);
  }
  const totals: Record<string, number> = {};
  for (const [p, set] of Object.entries(sets)) totals[p] = set.size;
  return totals;
}

/** One platform's view of one player. Null where the platform has no data. */
export interface PlayerPlatformStat {
  provider: string;
  /** Distinct drafts on this platform that roster the player. */
  drafts: number;
  /** Distinct drafts on this platform overall — the exposure denominator. */
  totalDrafts: number;
  /** Individual picks (a draft can only contain a player once, but be safe). */
  picks: number;
  /** drafts / totalDrafts, as a percentage. Null when the platform has no drafts. */
  exposure: number | null;
  /** Mean pick number on this platform. */
  myAdp: number | null;
  /** This platform's market ADP for the player. */
  marketAdp: number | null;
  /** myAdp − marketAdp, on this platform's own board. */
  clv: number | null;
  /** clv as a percentage of this platform's market ADP. */
  clvPct: number | null;
}

/**
 * Split one player's picks by platform, scoring each side against its own
 * market. Returns an entry for every platform loaded in the portfolio —
 * including ones the player was never drafted on (exposure 0), so the split
 * columns line up across rows.
 */
export function playerPlatformStats(
  player: Player,
  totals: Record<string, number>,
  lookupForProvider: (provider?: string) => Map<string, number>,
): PlayerPlatformStat[] {
  const key = canonicalName(player.name || "");
  const byProvider: Record<string, { drafts: Set<string>; picks: number[] }> = {};
  for (const row of player.rows) {
    const p = providerOf(row);
    const bucket = (byProvider[p] ??= { drafts: new Set(), picks: [] });
    if (row["Draft"]) bucket.drafts.add(row["Draft"]);
    const pick = Number(row["Pick Number"]);
    if (!isNaN(pick)) bucket.picks.push(pick);
  }

  return sortPlatforms(Object.keys(totals)).map(provider => {
    const bucket = byProvider[provider];
    const totalDrafts = totals[provider] ?? 0;
    const drafts = bucket ? bucket.drafts.size : 0;
    const picks = bucket ? bucket.picks.length : 0;
    const myAdp = picks > 0
      ? bucket!.picks.reduce((a, b) => a + b, 0) / picks
      : null;
    // Only claim a market ADP when this platform's board actually lists the
    // player; the undrafted floor is applied for CLV but never displayed as if
    // it were a real market price.
    const rawMarket = lookupForProvider(provider).get(key);
    const marketAdp = typeof rawMarket === "number" && !isNaN(rawMarket) ? rawMarket : null;
    const clvBase = resolveMarketAdp(rawMarket);
    const clv = myAdp !== null ? myAdp - clvBase : null;
    return {
      provider,
      drafts,
      totalDrafts,
      picks,
      exposure: totalDrafts > 0 ? (drafts / totalDrafts) * 100 : null,
      myAdp,
      marketAdp,
      clv,
      clvPct: clv !== null && clvBase !== 0 ? (clv / clvBase) * 100 : null,
    };
  });
}

/** Portfolio-level CLV summary for one platform (My Teams KPI strip). */
export interface TeamPlatformStat {
  provider: string;
  drafts: number;
  avgClv: number | null;
  bestClv: number | null;
  worstClv: number | null;
  avgProjection: number | null;
}

/**
 * Split a set of drafts by platform. A draft belongs to exactly one platform,
 * so this is a clean partition — unlike players, no draft is ever shared.
 */
export function teamPlatformStats(
  teams: Array<TeamDraft & { cumulativeCLV?: number; cumulativeProjection?: number }>,
): TeamPlatformStat[] {
  const buckets: Record<string, { clv: number[]; proj: number[] }> = {};
  for (const team of teams) {
    const p = team.provider || "Unknown";
    const b = (buckets[p] ??= { clv: [], proj: [] });
    if (typeof team.cumulativeCLV === "number" && !isNaN(team.cumulativeCLV)) b.clv.push(team.cumulativeCLV);
    if (typeof team.cumulativeProjection === "number" && !isNaN(team.cumulativeProjection)) b.proj.push(team.cumulativeProjection);
  }
  // Count drafts separately so a platform with no scorable CLV still appears.
  const draftCounts: Record<string, number> = {};
  for (const team of teams) {
    const p = team.provider || "Unknown";
    draftCounts[p] = (draftCounts[p] ?? 0) + 1;
  }
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
  return sortPlatforms(Object.keys(draftCounts)).map(provider => {
    const b = buckets[provider] ?? { clv: [], proj: [] };
    return {
      provider,
      drafts: draftCounts[provider],
      avgClv: mean(b.clv),
      bestClv: b.clv.length ? Math.max(...b.clv) : null,
      worstClv: b.clv.length ? Math.min(...b.clv) : null,
      avgProjection: mean(b.proj),
    };
  });
}
